/**
 * Settings Store - Reedle user preferences
 * Handles UI language and other user settings with Supabase sync
 */

import { observable } from '@legendapp/state';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { ui$ } from './ui$';
import i18n from '../i18n';
import { RESPONSE_LANGUAGES, UI_LANGUAGE_CODES, migrateLegacyLangCode } from '../constants/languages';

const DEFAULT_TRANSLATION_LANGUAGES = RESPONSE_LANGUAGES.map(({ code }) => code);

// Settings state
export const settings$ = observable({
  uiLanguage: null, // 'en' | 'zh' | 'ru' | null
  autoFetchContent: true, // Auto-fetch article content in background (default: true)
  useGeminiCleanup: true, // Use AI cleanup for articles (default: true = line-diff enabled)
  emailWeeklyReport: true, // Receive weekly report emails (default: true, opt-in)
  // AI preferences (global, shared across chat and reports)
  aiModel: 'gemini-3-flash-preview', // Selected AI model ID
  aiLanguage: 'en', // Actual language code when not following UI ('en'|'zh'|'ja'|'ru'|'fr')
  followUILanguage: true, // Whether AI responses follow the UI language (DB stores 'auto')
  translationLanguages: DEFAULT_TRANSLATION_LANGUAGES, // Languages offered by article translation controls
  chatThinkingEnabled: false, // Enhanced thinking for Google models in interactive chat
  webSearchEnabled: false, // Web search / grounding for AI responses (Gemini googleSearch, OpenAI web_search)
  // Prompt library — user custom prompts [{id, title, body, created_at}]
  customPrompts: [],
  // Developer mode: shows the AI module selector (localStorage only, not synced to DB)
  developerMode: (() => { try { return localStorage.getItem('reedle-dev-mode') === 'true'; } catch { return false; } })(),
  isLoading: false,
  settingsLoaded: false,   // True after first fetchSettings() completes
});

/**
 * Fetch user settings from database
 */
export const fetchSettings = async () => {
  const user = auth$.user.get();
  if (!user) {
    settings$.isLoading.set(false);
    return;
  }

  settings$.isLoading.set(true);

  try {
    const { data, error } = await supabase
      .from('reedle_user_settings')
      .select('setting_key, setting_value')
      .eq('user_id', user.id)
      .eq('deleted', false)
      .in('setting_key', ['ui_language', 'auto_fetch_content', 'use_gemini_cleanup', 'email_weekly_report', 'ai_model', 'ai_language', 'translation_languages', 'chat_thinking_enabled', 'web_search_enabled', 'onboarding_completed', 'custom_prompts']);

    if (error && error.code !== 'PGRST116') {
      console.error('Failed to fetch settings:', error);
    }

    // Process settings
    const settingsMap = {};
    data?.forEach(row => {
      settingsMap[row.setting_key] = row.setting_value;
    });

    // Apply ui_language
    const uiLang = settingsMap['ui_language'] || null;
    settings$.uiLanguage.set(uiLang);

    // Apply auto_fetch_content (default: true if not set)
    const autoFetch = settingsMap['auto_fetch_content'];
    settings$.autoFetchContent.set(autoFetch !== 'false'); // Default to true

    // Apply use_gemini_cleanup (default: true if not set)
    const geminiCleanup = settingsMap['use_gemini_cleanup'];
    settings$.useGeminiCleanup.set(geminiCleanup !== 'false'); // Default to true

    // Apply email_weekly_report (default: true if not set)
    const emailReport = settingsMap['email_weekly_report'];
    settings$.emailWeeklyReport.set(emailReport !== 'false'); // Default to true

    // Apply ai_model (default: gemini-3-flash-preview)
    const aiModel = settingsMap['ai_model'];
    if (aiModel) settings$.aiModel.set(aiModel);

    // Apply ai_language: 'auto' means follow UI language
    const aiLang = settingsMap['ai_language'];
    if (aiLang === 'auto') {
      settings$.followUILanguage.set(true);
      // aiLanguage stays as last known value (don't overwrite with 'auto')
    } else if (aiLang) {
      settings$.followUILanguage.set(false);
      settings$.aiLanguage.set(migrateLegacyLangCode(aiLang));
    }
    // If ai_language not set in DB, keep defaults (followUILanguage: true, aiLanguage: 'en')

    // Apply the translation-language allowlist. Missing values intentionally
    // preserve the previous product behavior: every supported language is shown.
    const translationLanguagesRaw = settingsMap['translation_languages'];
    settings$.translationLanguages.set([...DEFAULT_TRANSLATION_LANGUAGES]);
    if (translationLanguagesRaw) {
      try {
        const parsed = JSON.parse(translationLanguagesRaw);
        const validCodes = new Set(DEFAULT_TRANSLATION_LANGUAGES);
        const nextLanguages = Array.isArray(parsed)
          ? [...new Set(parsed)].filter(code => validCodes.has(code))
          : [];
        if (nextLanguages.length === 0) {
          throw new Error('translation_languages must contain at least one supported language');
        }
        settings$.translationLanguages.set(nextLanguages);
      } catch (err) {
        console.error('Failed to parse translation language settings:', err);
      }
    }

    // Apply chat_thinking_enabled (default: false)
    const thinkingEnabled = settingsMap['chat_thinking_enabled'];
    settings$.chatThinkingEnabled.set(thinkingEnabled === 'true');

    // Apply web_search_enabled (default: false)
    const webSearch = settingsMap['web_search_enabled'];
    settings$.webSearchEnabled.set(webSearch === 'true');

    // Apply custom_prompts (JSON array)
    const customPromptsRaw = settingsMap['custom_prompts'];
    if (customPromptsRaw) {
      try {
        const parsed = JSON.parse(customPromptsRaw);
        if (Array.isArray(parsed)) settings$.customPrompts.set(parsed);
      } catch {
        // Ignore malformed JSON
      }
    }

    // Apply onboarding_completed — sync DB state to local ui$ store
    if (settingsMap['onboarding_completed'] === 'true') {
      ui$.onboardingCompleted.set(true);
    } else if (ui$.onboardingCompleted.get()) {
      // Local says completed but DB missed the write — silently repair DB
      markOnboardingCompleted();
    }
  } catch (err) {
    console.error('Failed to fetch settings:', err);
  } finally {
    settings$.isLoading.set(false);
    settings$.settingsLoaded.set(true);
  }
};

/**
 * Update UI language setting
 * @param {string} langCode - Language code ('en', 'zh', 'ru')
 */
export const updateUILanguage = async (langCode) => {
  const user = auth$.user.get();
  if (!user) return;

  // Validate language code
  if (!UI_LANGUAGE_CODES.includes(langCode)) {
    console.error('Invalid language code:', langCode);
    return;
  }

  // Optimistic update
  settings$.uiLanguage.set(langCode);

  try {
    const { error } = await supabase
      .from('reedle_user_settings')
      .upsert({
        user_id: user.id,
        setting_key: 'ui_language',
        setting_value: langCode,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('Failed to update UI language:', error);
      // Note: Don't revert - i18n already changed, keep them in sync
    }
  } catch (err) {
    console.error('Failed to update UI language:', err);
  }
};

/**
 * Sync UI language from database on login
 * If DB has a preference, apply it. Otherwise, save current language to DB.
 * @param {string} userId - User ID
 */
export const syncUILanguageFromDB = async (userId) => {
  if (!userId) return;

  try {
    const { data, error } = await supabase
      .from('reedle_user_settings')
      .select('setting_value')
      .eq('user_id', userId)
      .eq('setting_key', 'ui_language')
      .eq('deleted', false)
      .single();

    if (error && error.code !== 'PGRST116') {
      // PGRST116 = no rows returned (setting doesn't exist yet)
      console.error('Failed to sync UI language:', error);
      return;
    }

    if (data?.setting_value) {
      // DB has a preference - apply it
      const dbLang = data.setting_value;
      if (UI_LANGUAGE_CODES.includes(dbLang)) {
        if (i18n.language !== dbLang) {
          i18n.changeLanguage(dbLang);
        }
        localStorage.setItem('reedle-ui-language', dbLang);
        settings$.uiLanguage.set(dbLang);
      }
    } else {
      // No DB preference - save current language to DB
      const currentLang = i18n.language?.split('-')[0] || 'en';
      const validLang = UI_LANGUAGE_CODES.includes(currentLang) ? currentLang : 'en';

      await supabase
        .from('reedle_user_settings')
        .upsert({
          user_id: userId,
          setting_key: 'ui_language',
          setting_value: validLang,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,setting_key'
        });

      settings$.uiLanguage.set(validLang);
    }
  } catch (err) {
    console.error('Failed to sync UI language:', err);
  }
};

/**
 * Get current UI language value
 * @returns {string|null}
 */
export const getUILanguage = () => {
  return settings$.uiLanguage.get();
};

/**
 * Update auto-fetch content setting
 * @param {boolean} enabled - Whether to auto-fetch article content in background
 */
export const updateAutoFetchContent = async (enabled) => {
  const user = auth$.user.get();
  if (!user) return;

  // Optimistic update
  settings$.autoFetchContent.set(enabled);

  try {
    const { error } = await supabase
      .from('reedle_user_settings')
      .upsert({
        user_id: user.id,
        setting_key: 'auto_fetch_content',
        setting_value: enabled ? 'true' : 'false',
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('Failed to update auto-fetch content setting:', error);
    }
  } catch (err) {
    console.error('Failed to update auto-fetch content setting:', err);
  }
};

/**
 * Get current auto-fetch content value
 * @returns {boolean}
 */
export const getAutoFetchContent = () => {
  return settings$.autoFetchContent.get();
};

/**
 * Update email weekly report setting
 * @param {boolean} enabled - Whether to receive weekly report emails
 */
export const updateEmailWeeklyReport = async (enabled) => {
  const user = auth$.user.get();
  if (!user) return;

  // Optimistic update
  settings$.emailWeeklyReport.set(enabled);

  try {
    const { error } = await supabase
      .from('reedle_user_settings')
      .upsert({
        user_id: user.id,
        setting_key: 'email_weekly_report',
        setting_value: enabled ? 'true' : 'false',
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('Failed to update email weekly report setting:', error);
    }
  } catch (err) {
    console.error('Failed to update email weekly report setting:', err);
  }
};

/**
 * Update Gemini cleanup setting
 * @param {boolean} enabled - Whether to use Gemini AI cleanup for articles
 */
export const updateUseGeminiCleanup = async (enabled) => {
  const user = auth$.user.get();
  if (!user) return;

  // Optimistic update
  settings$.useGeminiCleanup.set(enabled);

  try {
    const { error } = await supabase
      .from('reedle_user_settings')
      .upsert({
        user_id: user.id,
        setting_key: 'use_gemini_cleanup',
        setting_value: enabled ? 'true' : 'false',
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('Failed to update Gemini cleanup setting:', error);
    }
  } catch (err) {
    console.error('Failed to update Gemini cleanup setting:', err);
  }
};

/**
 * Helper: upsert a single key-value to reedle_user_settings
 */
const upsertSetting = async (userId, key, value) => {
  const { error } = await supabase
    .from('reedle_user_settings')
    .upsert({
      user_id: userId,
      setting_key: key,
      setting_value: value,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,setting_key' });
  if (error) console.error(`Failed to update setting ${key}:`, error);
};

/**
 * Update preferred AI model (shared across chat and reports)
 * @param {string} modelId - Model ID string
 */
export const updateAIModel = async (modelId) => {
  const user = auth$.user.get();
  if (!user) return;
  settings$.aiModel.set(modelId);
  try { await upsertSetting(user.id, 'ai_model', modelId); } catch (err) { console.error('Failed to update AI model:', err); }
};

/**
 * Update preferred AI response language.
 * Sets followUILanguage to false and persists the specific language code.
 * @param {string} langCode - e.g. 'en' | 'zh' | 'ja' | 'ru' | 'fr'
 */
export const updateAILanguage = async (langCode) => {
  const user = auth$.user.get();
  if (!user) return;
  settings$.aiLanguage.set(langCode);
  settings$.followUILanguage.set(false);
  try { await upsertSetting(user.id, 'ai_language', langCode); } catch (err) { console.error('Failed to update AI language:', err); }
};

/**
 * Update "follow UI language" toggle.
 * When true, stores 'auto' in DB so server-side jobs use ui_language.
 * When false, stores the current aiLanguage value.
 * @param {boolean} enabled
 */
export const updateFollowUILanguage = async (enabled) => {
  const user = auth$.user.get();
  if (!user) return;
  settings$.followUILanguage.set(enabled);
  const dbValue = enabled ? 'auto' : settings$.aiLanguage.get();
  try { await upsertSetting(user.id, 'ai_language', dbValue); } catch (err) { console.error('Failed to update follow UI language:', err); }
};

/**
 * Update the languages offered by article translation controls.
 * At least one language must remain selected so Translate never becomes a dead action.
 * @param {string[]} langCodes
 */
export const updateTranslationLanguages = async (langCodes) => {
  const supportedCodes = new Set(DEFAULT_TRANSLATION_LANGUAGES);
  if (!Array.isArray(langCodes) || langCodes.length === 0) {
    throw new TypeError('Translation languages must be a non-empty array.');
  }

  const nextLanguages = [...new Set(langCodes)];
  if (nextLanguages.some(code => !supportedCodes.has(code))) {
    throw new TypeError('Translation languages contain an unsupported code.');
  }

  const user = auth$.user.get();
  if (!user) return;
  settings$.translationLanguages.set(nextLanguages);
  await upsertSetting(user.id, 'translation_languages', JSON.stringify(nextLanguages));
};

/**
 * Update enhanced thinking toggle for interactive AI chat (Google models only).
 * @param {boolean} enabled
 */
export const updateChatThinking = async (enabled) => {
  const user = auth$.user.get();
  if (!user) return;
  settings$.chatThinkingEnabled.set(enabled);
  try { await upsertSetting(user.id, 'chat_thinking_enabled', enabled ? 'true' : 'false'); } catch (err) { console.error('Failed to update chat thinking:', err); }
};

/**
 * Update web search / grounding toggle for AI chat.
 * When enabled, AI responses include real-time web results (Gemini/OpenAI only).
 * @param {boolean} enabled
 */
export const updateWebSearch = async (enabled) => {
  const user = auth$.user.get();
  if (!user) return;
  settings$.webSearchEnabled.set(enabled);
  try { await upsertSetting(user.id, 'web_search_enabled', enabled ? 'true' : 'false'); } catch (err) { console.error('Failed to update web search:', err); }
};

/**
 * Update developer mode toggle (localStorage only — not synced to Supabase).
 * @param {boolean} enabled
 */
export const updateDeveloperMode = (enabled) => {
  settings$.developerMode.set(enabled);
  try {
    if (enabled) {
      localStorage.setItem('reedle-dev-mode', 'true');
    } else {
      localStorage.removeItem('reedle-dev-mode');
    }
  } catch { /* ignore */ }
};

/**
 * Save custom prompts array to the database.
 * Optimistically updates local state then persists as JSON string.
 * @param {Array<{id: string, title: string, body: string, created_at: string}>} prompts
 */
export const saveCustomPrompts = async (prompts) => {
  const user = auth$.user.get();
  if (!user) return;
  settings$.customPrompts.set(prompts);
  try {
    await upsertSetting(user.id, 'custom_prompts', JSON.stringify(prompts));
  } catch (err) {
    console.error('Failed to save custom prompts:', err);
  }
};

/**
 * Persist onboarding completion to the database.
 * Local ui$ state must be set by the caller before invoking this.
 * Retries once on failure to handle transient network errors.
 * No-op if the user is not authenticated.
 */
export const markOnboardingCompleted = async () => {
  const user = auth$.user.get();
  if (!user) return;
  const write = () => upsertSetting(user.id, 'onboarding_completed', 'true');
  try {
    await write();
  } catch (err) {
    console.error('Failed to persist onboarding completion, retrying:', err);
    try { await write(); } catch (err2) { console.error('Onboarding completion retry failed:', err2); }
  }
};
